/**
 * @type Feature
 * @rationale Single, shared way to turn any API/JS error into safe, user-facing
 *   fields (message, hint, code, request id) plus a copyable summary. Replaces the
 *   scattered per-module error parsers so every surface reads errors the same way.
 * @preserved_behavior Legacy string `detail`, FastAPI validation arrays, and
 *   `response.data.message` still resolve to the same message text as before.
 *   Domain-specific structured payloads (blocked-driver, conflict) are NOT treated
 *   as error envelopes — only objects with BOTH `code` and `request_id` are.
 */
import axios from 'axios';
import { CLIENT_OUTDATED_MESSAGE, SERVER_OUTDATED_MESSAGE } from './scheduleWriteContract';

export interface AppErrorDetails {
  message: string;
  hint?: string;
  code?: string;
  params?: Record<string, unknown>;
  requestId?: string;
  status?: number;
  route?: string;
  /** Safe, allowlisted, multi-line text for a "Copy details" button. */
  copyText: string;
}

export interface ParseAppErrorOptions {
  /** Message to show when nothing more specific is available. */
  fallback?: string;
  /** Human page name included in copy details (never PII). */
  page?: string;
}

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const NETWORK_MESSAGE = 'Unable to reach the server. Check your connection and try again.';
const SAFE_DIAGNOSTIC_PARAM_KEYS = ['reason', 'attempts'] as const;
const MAX_SAFE_DIAGNOSTIC_PARAM_VALUE_LENGTH = 160;

type SafeDiagnosticParamValue = string | number | boolean;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readEnvelopeParams(params: unknown): Record<string, unknown> | undefined {
  return isRecord(params) ? params : undefined;
}

function isSafeDiagnosticParamValue(value: unknown): value is SafeDiagnosticParamValue {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function truncateSafeDiagnosticParamValue(value: SafeDiagnosticParamValue): SafeDiagnosticParamValue {
  if (typeof value !== 'string' || value.length <= MAX_SAFE_DIAGNOSTIC_PARAM_VALUE_LENGTH) {
    return value;
  }
  return `${value.slice(0, MAX_SAFE_DIAGNOSTIC_PARAM_VALUE_LENGTH)}…`;
}

/** Keeps only the short C2 diagnostics that are safe to show to a dispatcher. */
export function normalizeSafeDiagnosticParams(
  params: unknown,
): Record<string, SafeDiagnosticParamValue> | undefined {
  if (!isRecord(params)) return undefined;

  const normalized: Record<string, SafeDiagnosticParamValue> = {};
  for (const key of SAFE_DIAGNOSTIC_PARAM_KEYS) {
    const value = params[key];
    if (isSafeDiagnosticParamValue(value)) {
      normalized[key] = truncateSafeDiagnosticParamValue(value);
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

interface StructuredEnvelope {
  code: string;
  message: string;
  request_id: string;
  hint?: string;
  params?: unknown;
}

type TechnicalDetails = Pick<
  AppErrorDetails,
  'hint' | 'code' | 'params' | 'requestId' | 'status' | 'route'
>;

const NO_NESTED_ERROR = Symbol('NO_NESTED_ERROR');

/**
 * The discriminator (contract): a `detail` is the new error envelope ONLY when it
 * is a plain object carrying BOTH a `code` and a `request_id`. This deliberately
 * excludes existing domain payloads like `{code: "conflict", message}` (no
 * request_id) and `{blocked, driver_name, message}`.
 */
function isEnvelope(detail: unknown): detail is StructuredEnvelope {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    !Array.isArray(detail) &&
    typeof (detail as Record<string, unknown>).code === 'string' &&
    typeof (detail as Record<string, unknown>).request_id === 'string' &&
    typeof (detail as Record<string, unknown>).message === 'string'
  );
}

function summarizeValidationArray(detail: unknown[]): string {
  return detail
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'msg' in item) {
        const record = item as { msg?: unknown; loc?: unknown };
        const loc = Array.isArray(record.loc)
          ? record.loc.filter((p) => p !== 'body').join('.')
          : '';
        const msg = String(record.msg ?? '');
        return loc ? `${loc}: ${msg}` : msg;
      }
      return '';
    })
    .filter(Boolean)
    .join('; ');
}

function isOldBackendContractRefusal(status: number | undefined, detail: unknown): boolean {
  if (status !== 422) return false;
  if (typeof detail === 'string') {
    return detail === 'contract_version: Extra inputs are not permitted';
  }
  if (isRecord(detail)) {
    return detail.code === 'VALIDATION_ERROR'
      && detail.message === 'contract_version: Extra inputs are not permitted';
  }
  if (!Array.isArray(detail) || detail.length !== 1) return false;
  const item = detail[0];
  if (!isRecord(item) || !Array.isArray(item.loc)) return false;
  return item.loc.length === 2
    && item.loc[0] === 'body'
    && item.loc[1] === 'contract_version'
    && (item.type === 'extra_forbidden' || item.type === 'value_error.extra');
}

function readHeaderRequestId(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  // Stashed by the interceptor, or read straight off the response headers.
  const stashed = (error as { requestId?: string }).requestId;
  if (stashed) return stashed;
  const headers = error.response?.headers as Record<string, string> | undefined;
  return headers?.['x-request-id'] ?? headers?.['X-Request-ID'];
}

function readNestedError(error: Error): unknown | typeof NO_NESTED_ERROR {
  const wrappedError = error as Error & { readonly originalError?: unknown };
  if ('originalError' in wrappedError) return wrappedError.originalError;
  if ('cause' in error) return error.cause;
  return NO_NESTED_ERROR;
}

function readEnvelopeTechnicalDetails(error: unknown): TechnicalDetails | undefined {
  if (!axios.isAxiosError(error)) return undefined;

  const data = error.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;
  if (!isEnvelope(detail)) return undefined;

  return {
    hint: detail.hint,
    code: detail.code,
    params: readEnvelopeParams(detail.params),
    requestId: detail.request_id,
    status: error.response?.status,
    route: error.config?.url ?? undefined,
  };
}

function findNestedEnvelope(
  error: unknown,
  depth: number,
  seen: WeakSet<object>,
): TechnicalDetails | undefined {
  if (depth > 3) return undefined;

  if (typeof error === 'object' && error !== null) {
    if (seen.has(error)) return undefined;
    seen.add(error);
  }

  const technicalDetails = readEnvelopeTechnicalDetails(error);
  if (technicalDetails) return technicalDetails;
  if (!(error instanceof Error)) return undefined;

  const nestedError = readNestedError(error);
  return nestedError === NO_NESTED_ERROR
    ? undefined
    : findNestedEnvelope(nestedError, depth + 1, seen);
}

function buildCopyText(details: Omit<AppErrorDetails, 'copyText'> & { page?: string }): string {
  const lines: string[] = [details.message];
  if (details.hint) lines.push(`Hint: ${details.hint}`);
  const params = normalizeSafeDiagnosticParams(details.params);
  if (params?.reason !== undefined) lines.push(`Reason: ${params.reason}`);
  if (params?.attempts !== undefined) lines.push(`Attempts: ${params.attempts}`);
  if (details.code) lines.push(`Code: ${details.code}`);
  if (details.requestId) lines.push(`Request ID: ${details.requestId}`);
  if (typeof details.status === 'number') lines.push(`Status: ${details.status}`);
  if (details.route) lines.push(`Route: ${details.route}`);
  if (details.page) lines.push(`Page: ${details.page}`);
  lines.push(`Time: ${new Date().toISOString()}`);
  return lines.join('\n');
}

export function parseAppError(
  error: unknown,
  options: ParseAppErrorOptions = {},
): AppErrorDetails {
  const fallback = options.fallback ?? GENERIC_MESSAGE;

  let message = fallback;
  let hint: string | undefined;
  let code: string | undefined;
  let params: Record<string, unknown> | undefined;
  let requestId: string | undefined;
  let status: number | undefined;
  let route: string | undefined;

  if (axios.isAxiosError(error)) {
    status = error.response?.status;
    route = error.config?.url ?? undefined;
    requestId = readHeaderRequestId(error);

    const data = error.response?.data as { detail?: unknown; message?: unknown } | undefined;
    const detail = data?.detail;

    if (isEnvelope(detail)) {
      message = isOldBackendContractRefusal(status, detail)
        ? SERVER_OUTDATED_MESSAGE
        : detail.code === 'CLIENT_OUTDATED'
        ? CLIENT_OUTDATED_MESSAGE
        : detail.code === 'SERVER_OUTDATED'
          ? SERVER_OUTDATED_MESSAGE
          : detail.message || fallback;
      hint = detail.hint;
      code = detail.code;
      params = readEnvelopeParams(detail.params);
      requestId = detail.request_id ?? requestId;
    } else if (typeof detail === 'string' && detail.trim()) {
      message = isOldBackendContractRefusal(status, detail)
        ? SERVER_OUTDATED_MESSAGE
        : detail;
    } else if (Array.isArray(detail) && detail.length > 0) {
      message = isOldBackendContractRefusal(status, detail)
        ? SERVER_OUTDATED_MESSAGE
        : summarizeValidationArray(detail) || fallback;
    } else if (
      detail &&
      typeof detail === 'object' &&
      !Array.isArray(detail) &&
      typeof (detail as { message?: unknown }).message === 'string' &&
      (detail as { message: string }).message.trim()
    ) {
      // Non-envelope domain object (e.g. blocked-driver, conflict) — surface its
      // safe author-written message rather than the generic fallback. We still do
      // NOT treat it as an envelope (no code/request_id), so specialized UIs that
      // read the object directly keep working.
      message = (detail as { message: string }).message;
    } else if (data && typeof data.message === 'string' && data.message.trim()) {
      message = data.message;
    } else if (error.code === 'ERR_NETWORK') {
      message = NETWORK_MESSAGE;
    } else if (status) {
      message = fallback;
    } else {
      message = error.message || fallback;
    }
  } else if (error instanceof Error) {
    message = error.message || fallback;
  }

  if (error instanceof Error) {
    const nestedError = readNestedError(error);
    if (nestedError !== NO_NESTED_ERROR) {
      const seen = new WeakSet<object>();
      seen.add(error);
      // Only a nested envelope may replace these fields. Assigning
      // unconditionally would erase an outer Axios error's own code and request
      // id whenever the wrapper carries a cause that has no envelope of its own
      // (AxiosError.from defines `cause`, so an Axios error can reach here).
      const technicalDetails = findNestedEnvelope(nestedError, 1, seen);
      if (technicalDetails) {
        hint = technicalDetails.hint;
        code = technicalDetails.code;
        params = technicalDetails.params;
        requestId = technicalDetails.requestId;
        status = technicalDetails.status;
        route = technicalDetails.route;
      }
    }
  }

  const copyText = buildCopyText({
    message,
    hint,
    code,
    params,
    requestId,
    status,
    route,
    page: options.page,
  });

  return { message, hint, code, params, requestId, status, route, copyText };
}

/** Convenience: the plain user-facing message only. */
export function getAppErrorMessage(error: unknown, fallback?: string): string {
  return parseAppError(error, { fallback }).message;
}

/** 401 — the session is missing/expired; the app should re-authenticate. */
export function isAuthError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

/** 403 — authenticated but not allowed; show a plain permission message. */
export function isPermissionError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}
